"use client";

import { add, format } from "date-fns";
import { enUS } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { useRef } from "react";
import { Button } from "../button";
import { cn } from "../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Calendar } from "./calendar";
import { TimePicker } from "./time-picker";
import type { DateTimePickerProps, DateTimePickerRef } from "./types";

const DateTimePicker = React.forwardRef<
	Partial<DateTimePickerRef>,
	DateTimePickerProps
>(
	(
		{
			locale = enUS,
			defaultPopupValue = new Date(new Date().setHours(0, 0, 0, 0)),
			value,
			onChange,
			onMonthChange,
			hourCycle = 24,
			yearRange = 50,
			disabled = false,
			displayFormat,
			granularity = "second",
			placeholder = "Pick a date",
			className,
			...props
		},
		ref,
	) => {
		const [month, setMonth] = React.useState<Date>(value ?? defaultPopupValue);
		const buttonRef = useRef<HTMLButtonElement>(null);
		const [displayDate, setDisplayDate] = React.useState<Date | undefined>(
			value ?? undefined,
		);
		onMonthChange ||= onChange;
		/**
		 * carry over the current time when a user clicks a new day
		 * instead of resetting to 00:00
		 */
		const handleMonthChange = (newDay: Date | undefined) => {
			if (!newDay) {
				return;
			}
			if (!defaultPopupValue) {
				newDay.setHours(
					month?.getHours() ?? 0,
					month?.getMinutes() ?? 0,
					month?.getSeconds() ?? 0,
				);
				onMonthChange?.(newDay);
				setMonth(newDay);
				return;
			}
			const diff = newDay.getTime() - defaultPopupValue.getTime();
			const diffInDays = diff / (1000 * 60 * 60 * 24);
			const newDateFull = add(defaultPopupValue, {
				days: Math.ceil(diffInDays),
			});
			newDateFull.setHours(
				month?.getHours() ?? 0,
				month?.getMinutes() ?? 0,
				month?.getSeconds() ?? 0,
			);
			onMonthChange?.(newDateFull);
			setMonth(newDateFull);
		};

		const onSelect = (newDay?: Date) => {
			if (!newDay) {
				return;
			}
			onChange?.(newDay);
			setMonth(newDay);
			setDisplayDate(newDay);
		};

		React.useImperativeHandle(
			ref,
			() => ({
				...buttonRef.current,
				value: displayDate,
			}),
			[displayDate],
		);

		const initHourFormat = {
			hour24:
				displayFormat?.hour24 ??
				`PPP HH:mm${!granularity || granularity === "second" ? ":ss" : ""}`,
			hour12:
				displayFormat?.hour12 ??
				`PP hh:mm${!granularity || granularity === "second" ? ":ss" : ""} b`,
		};

		let loc = enUS;
		const { options, localize, formatLong } = locale;
		if (options && localize && formatLong) {
			loc = {
				...enUS,
				options,
				localize,
				formatLong,
			};
		}

		return (
			<Popover>
				<PopoverTrigger asChild disabled={disabled}>
					<Button
						intent="outline"
						className={cn(
							"w-full justify-start text-left font-normal",
							!displayDate && "text-muted-foreground",
							className,
						)}
						ref={buttonRef}
					>
						<CalendarIcon className="mr-2 h-4 w-4" />
						{displayDate ? (
							format(
								displayDate,
								hourCycle === 24
									? initHourFormat.hour24
									: initHourFormat.hour12,
								{
									locale: loc,
								},
							)
						) : (
							<span>{placeholder}</span>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0">
					<Calendar
						mode="single"
						selected={displayDate}
						month={month}
						onSelect={(newDate) => {
							if (newDate) {
								newDate.setHours(
									month?.getHours() ?? 0,
									month?.getMinutes() ?? 0,
									month?.getSeconds() ?? 0,
								);
								onSelect(newDate);
							}
						}}
						onMonthChange={handleMonthChange}
						yearRange={yearRange}
						locale={locale}
						{...props}
					/>
					{granularity !== "day" && (
						<div className="border-t border-border p-3">
							<TimePicker
								onChange={(value) => {
									onChange?.(value);
									setDisplayDate(value);
									if (value) {
										setMonth(value);
									}
								}}
								date={month}
								hourCycle={hourCycle}
								granularity={granularity}
							/>
						</div>
					)}
				</PopoverContent>
			</Popover>
		);
	},
);

DateTimePicker.displayName = "DateTimePicker";

const InlineDateTimePicker = React.forwardRef<
	Partial<DateTimePickerRef>,
	Omit<DateTimePickerProps, "placeholder" | "className"> & {
		className?: string;
	}
>(
	(
		{
			locale = enUS,
			defaultPopupValue = new Date(new Date().setHours(0, 0, 0, 0)),
			value,
			onChange,
			onMonthChange,
			hourCycle = 24,
			yearRange = 50,
			disabled = false,
			displayFormat,
			granularity = "second",
			className,
			...props
		},
		ref,
	) => {
		const [month, setMonth] = React.useState<Date>(value ?? defaultPopupValue);
		const containerRef = useRef<HTMLDivElement>(null);
		const [displayDate, setDisplayDate] = React.useState<Date | undefined>(
			value ?? undefined,
		);
		onMonthChange ||= onChange;

		const handleMonthChange = (newDay: Date | undefined) => {
			if (!newDay) {
				return;
			}
			if (!defaultPopupValue) {
				newDay.setHours(
					month?.getHours() ?? 0,
					month?.getMinutes() ?? 0,
					month?.getSeconds() ?? 0,
				);
				onMonthChange?.(newDay);
				setMonth(newDay);
				return;
			}
			const diff = newDay.getTime() - defaultPopupValue.getTime();
			const diffInDays = diff / (1000 * 60 * 60 * 24);
			const newDateFull = add(defaultPopupValue, {
				days: Math.ceil(diffInDays),
			});
			newDateFull.setHours(
				month?.getHours() ?? 0,
				month?.getMinutes() ?? 0,
				month?.getSeconds() ?? 0,
			);
			onMonthChange?.(newDateFull);
			setMonth(newDateFull);
		};

		const onSelect = (newDay?: Date) => {
			if (!newDay) {
				return;
			}
			onChange?.(newDay);
			setMonth(newDay);
			setDisplayDate(newDay);
		};

		React.useImperativeHandle(ref, () => ({ value: displayDate }), [
			displayDate,
		]);

		return (
			<div ref={containerRef} className={cn("w-auto relative", className)}>
				<Calendar
					mode="single"
					selected={displayDate}
					month={month}
					onSelect={(newDate) => {
						if (newDate) {
							newDate.setHours(
								month?.getHours() ?? 0,
								month?.getMinutes() ?? 0,
								month?.getSeconds() ?? 0,
							);
							onSelect(newDate);
						}
					}}
					onMonthChange={handleMonthChange}
					yearRange={yearRange}
					locale={locale}
					{...props}
				/>
				{granularity !== "day" && (
					<div className="border-t border-border p-3">
						<TimePicker
							onChange={(value) => {
								onChange?.(value);
								setDisplayDate(value);
								if (value) {
									setMonth(value);
								}
							}}
							date={month}
							hourCycle={hourCycle}
							granularity={granularity}
						/>
					</div>
				)}
			</div>
		);
	},
);

InlineDateTimePicker.displayName = "InlineDateTimePicker";

export { DateTimePicker, InlineDateTimePicker };
